import { FunctionComponent, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { faLock } from "@fortawesome/pro-regular-svg-icons";

import { Icon } from "@/components";
import { PARSED_ENV } from "@/constants/common";

import { CopyBlock } from "./CopyBlock";
import {
	buildMcpEndpoint,
	MCP_CAPABILITY_KEYS,
	MCP_CLIENT_SNIPPETS,
	MCP_RATE_LIMIT_REQUESTS,
	MCP_RATE_LIMIT_WINDOW_SECONDS,
} from "./constants";

const SectionHeading: FunctionComponent<{ children: string }> = ({ children }) => (
	<h2 className="text-text text-md font-semibold">{children}</h2>
);

/**
 * Documentation page for the Signalist MCP server: what it is, the endpoint, how to add it to
 * popular MCP clients, and what it can do. Rendered from IntegrationDetail for the `mcp` provider
 * and gated by the MCP_ACCESS feature flag at the route level.
 *
 * There is no credential to show: clients authenticate with OAuth 2.1, so the user approves the
 * connection in the browser and manages it afterwards under Account -> Connected Accounts.
 */
export const McpServerGuide: FunctionComponent = () => {
	const { t } = useTranslation();

	const endpoint = useMemo(() => buildMcpEndpoint(PARSED_ENV.VITE_API_URL), []);

	return (
		<div className="flex max-w-3xl flex-col gap-8">
			<section className="flex flex-col gap-2">
				<SectionHeading>{t("integrations.mcp.overview.title")}</SectionHeading>
				<p className="text-text-meta text-sm leading-relaxed">
					{t("integrations.mcp.overview.body")}
				</p>
			</section>

			<section className="flex flex-col gap-3">
				<SectionHeading>{t("integrations.mcp.connection.title")}</SectionHeading>
				<CopyBlock
					label={t("integrations.mcp.connection.endpointLabel")}
					testId="mcp-endpoint-copy"
					value={endpoint}
				/>
				<p className="text-text-meta text-sm leading-relaxed">
					{t("integrations.mcp.connection.authNote")}
				</p>
			</section>

			<section className="flex flex-col gap-4">
				<div className="flex flex-col gap-1">
					<SectionHeading>{t("integrations.mcp.clients.title")}</SectionHeading>
					<p className="text-text-meta text-sm leading-relaxed">
						{t("integrations.mcp.clients.intro")}
					</p>
				</div>

				{MCP_CLIENT_SNIPPETS.map((snippet) => (
					<div key={snippet.id} className="flex flex-col gap-2">
						<h3 className="text-text text-sm font-medium">{t(snippet.titleKey)}</h3>
						<p className="text-text-meta text-sm leading-relaxed">{t(snippet.descriptionKey)}</p>
						<CopyBlock
							label={snippet.language}
							testId={`mcp-snippet-${snippet.id}`}
							value={snippet.build(endpoint)}
						/>
					</div>
				))}
			</section>

			<section className="flex flex-col gap-3">
				<SectionHeading>{t("integrations.mcp.capabilities.title")}</SectionHeading>
				<ul className="flex flex-col gap-2">
					{MCP_CAPABILITY_KEYS.map((key) => (
						<li key={key} className="text-text-meta flex items-start gap-2 text-sm leading-relaxed">
							<span className="bg-brand-light mt-1.5 size-1.5 shrink-0 rounded-full" />
							<span>{t(key)}</span>
						</li>
					))}
				</ul>
			</section>

			<section className="flex items-start gap-3 rounded-lg border border-border-light bg-background-light p-4">
				<Icon className="text-brand-light mt-0.5" icon={faLock} />
				<div className="flex flex-col gap-1">
					<h3 className="text-text text-sm font-medium">{t("integrations.mcp.security.title")}</h3>
					<p className="text-text-meta text-sm leading-relaxed">
						{t("integrations.mcp.security.body", {
							requests: MCP_RATE_LIMIT_REQUESTS,
							seconds: MCP_RATE_LIMIT_WINDOW_SECONDS,
						})}
					</p>
				</div>
			</section>
		</div>
	);
};
